/**
 * A prancheta de construção (N3/N4).
 *
 * REESCRITA COM O GESTO NO CENTRO. A régua e o compasso de verdade não se usam com
 * dois cliques: encosta-se a ponta e arrasta-se. Aqui é igual — pressionar, arrastar,
 * soltar cria o objeto num movimento só, e a sequência de cliques continua valendo
 * para quem prefere, para o toque preciso e para o teclado.
 *
 * Três decisões estruturais, todas com consequência visível:
 *
 * 1. DUAS CAMADAS. A construção (cara de desenhar, muda pouco) e a camada de apoio
 *    — cursor, ímã, prévia, nascimento — (barata, muda a cada quadro) vivem em SVGs
 *    separados. Antes, mover o ponteiro redesenhava a cena inteira, `evaluate()`
 *    incluso, sessenta vezes por segundo. Agora `evaluate()` roda quando o DAG muda.
 *
 * 2. O ÍMÃ TEM FORMA, NÃO SÓ POSIÇÃO. Cada proveniência de snap tem um marcador
 *    diferente: cruz para interseção, losango para "sobre o objeto", anel cheio para
 *    ponto existente, anel PONTILHADO para o vazio. Essa distinção é o coração da
 *    correção — um ponto derivado sobrevive à perturbação dos dados, um ponto largado
 *    no vazio não — e o aluno precisa vê-la ANTES de soltar, não depois de errar.
 *
 * 3. O ENQUADRAMENTO NÃO PULA. O `viewBox` é calculado uma vez e só muda por ação
 *    explícita (zoom, arrasto do quadro, reenquadrar). Um enquadramento que se
 *    recalcula a cada arco traçado faz a figura pular sob o dedo do aluno e o faz
 *    errar cliques por culpa da interface.
 *
 * A lógica de criação continua em `Tool.apply`, que é PURA e não foi tocada: aqui só
 * se cuida de eventos, coordenadas e desenho.
 */

import type {
  ConstructionSnapshot,
  EvaluationResult,
  GeoId,
  GeoObject,
} from '../geom/construction.ts';
import { evaluate, intersectionPoints, removeObject } from '../geom/construction.ts';
import { pontoNoModelo } from './coords.ts';
import { nome, numeroDe } from '../core/pt.ts';
import type { SnapConfig, SnapResult } from '../geom/snapping.ts';
import { snap, snapLabel } from '../geom/snapping.ts';
import type { CreatingToolId, PickRef, Tool, ToolAction, ToolId } from '../geom/tools.ts';
import { movimentosEfetivos, TOOLS } from '../geom/tools.ts';
import type { Vec2 } from '../geom/vec.ts';
import { add, angleOf, dist, midpoint, normalize, perp, scale, sub } from '../geom/vec.ts';
import { prefersReducedMotion } from '../render/animate.ts';
import { constructionToScene } from '../render/geom-scene.ts';
import { boundsOf, scene, type Rect, type Shape } from '../render/scene.ts';
import { renderScene } from '../render/svg.ts';
import { icone, META } from './toolIcons.ts';

export interface PadOptions {
  readonly givens: ConstructionSnapshot;
  readonly toolbelt: readonly ToolId[];
  readonly snapping: SnapConfig;
  readonly givenIds: ReadonlySet<string>;
  /** Camada decorativa desenhada sob tudo; não participa do DAG nem do snap. */
  readonly backdrop?: readonly Shape[];
  /** Enquadramento inicial; se ausente, sai dos `givens`. */
  readonly viewBox?: Rect;
  /** Descrição da área de trabalho para leitores de tela. */
  readonly describe?: string;
  /** Dar letra aos pontos criados. Padrão: sim. */
  readonly rotularPontos?: boolean;
  /** Com exatamente este número de pontos marcados, ligá-los num polígono (só desenho). */
  readonly ligarPontos?: number;
  readonly onAction?: (action: ToolAction) => void;
  readonly onToolSelected?: (tool: ToolId) => void;
  readonly onChange?: (snapshot: ConstructionSnapshot) => void;
}

export interface ConstructionPad {
  readonly element: HTMLElement;
  snapshot(): ConstructionSnapshot;
  /** Uma CÓPIA da trajetória: quem a guarda não vê as ações que vierem depois. */
  trajectory(): readonly ToolAction[];
  /**
   * Trava a construção: nada mais se cria, apaga ou desfaz. Aproximar e mover o quadro
   * continuam valendo — olhar não muda a resposta.
   */
  bloquear(): void;
  dispose(): void;
}

/** Curto de propósito: quem tem pressa não deve esperar a animação terminar. */
const DUR_NASCIMENTO = 210;
const DUR_IMA = 130;
/** Abaixo disto o gesto é um toque, não um arrasto. Em pixels de tela. */
const LIMIAR_ARRASTO = 7;
const ZOOM_MIN = 0.25;
const ZOOM_MAX = 8;

/** Conta as pranchetas criadas, para dar a cada uma um id próprio. */
let pranchetas = 0;

export function createConstructionPad(opts: PadOptions): ConstructionPad {
  const reduzido = prefersReducedMotion();

  // --- esqueleto -------------------------------------------------------------
  const root = document.createElement('div');
  root.className = 'pad';

  const barra = document.createElement('div');
  barra.className = 'pad-barra';
  barra.setAttribute('role', 'toolbar');
  barra.setAttribute('aria-label', 'Ferramentas de construção');

  const palco = document.createElement('div');
  palco.className = 'pad-palco';
  palco.tabIndex = 0;
  palco.setAttribute('aria-label', 'Área de construção');

  const capaBase = document.createElement('div');
  capaBase.className = 'pad-capa pad-capa-base';
  const capaSobre = document.createElement('div');
  capaSobre.className = 'pad-capa pad-capa-sobre';

  const etiqueta = document.createElement('span');
  etiqueta.className = 'pad-etiqueta';
  etiqueta.hidden = true;

  const contador = document.createElement('span');
  contador.className = 'pad-contador';
  contador.title = 'Movimentos usados';

  const nav = document.createElement('div');
  nav.className = 'pad-nav';

  palco.append(capaBase, capaSobre, etiqueta, contador, nav);

  const dica = document.createElement('p');
  dica.className = 'pad-dica';
  const passos = document.createElement('span');
  passos.className = 'pad-passos';
  const dicaTexto = document.createElement('span');
  dicaTexto.className = 'pad-dica-texto';
  dicaTexto.setAttribute('aria-live', 'polite');
  dica.append(passos, dicaTexto);

  const explicacao = document.createElement('div');
  explicacao.className = 'pad-explicacao';
  // Um id por prancheta: as vistas não se destroem ao trocar de aba, e com a questão do
  // percurso e a do laboratório abertas havia dois `pad-explicacao` na página — o
  // `aria-describedby` dos botões apontava para o primeiro, o da vista escondida.
  explicacao.id = `pad-explicacao-${++pranchetas}`;
  explicacao.hidden = true;
  explicacao.setAttribute('role', 'tooltip');

  root.append(barra, palco, dica, explicacao);

  // --- estado ----------------------------------------------------------------
  let atual: ConstructionSnapshot = opts.givens;
  let avaliado: EvaluationResult = evaluate(atual);
  const historico: ConstructionSnapshot[] = [opts.givens];
  const acoes: ToolAction[] = [];
  let ferramenta: ToolId = ferramentaInicial(opts.toolbelt);
  let escolhas: PickRef[] = [];
  let cursor: Vec2 | null = null;
  let snapAtual: SnapResult | null = null;
  let snapChave = '';
  let snapEm = 0;
  let nascendo: { readonly ids: readonly GeoId[]; readonly t0: number } | null = null;
  /** Resposta enviada: a construção não aceita mais mudanças. */
  let bloqueado = false;
  const t0 = performance.now();

  // --- enquadramento ---------------------------------------------------------
  const caixaConteudo: Rect =
    opts.viewBox ??
    folga(
      boundsOf([...formasDoSnapshot(opts.givens), ...(opts.backdrop ?? [])], 0.08),
      folgaDoCinto(opts.toolbelt),
    );

  let larguraPx = 0;
  let aspecto = 1.5;
  /**
   * O aluno já mexeu no enquadramento (zoom, arrasto do quadro)?
   *
   * Enquanto não mexeu, o quadro SEGUE o tamanho do palco: se a primeira medida veio
   * de um leiaute ainda em transição, a próxima corrige, em vez de a figura ficar
   * pequena num canto para sempre. Depois que ele mexeu, a regra antiga vale — o
   * enquadramento só muda por ação dele.
   */
  let enquadramentoTocado = false;
  let centro: Vec2 = {
    x: caixaConteudo.x + caixaConteudo.w / 2,
    y: caixaConteudo.y + caixaConteudo.h / 2,
  };
  let larguraVista = Math.max(caixaConteudo.w, caixaConteudo.h * aspecto);

  const vista = (): Rect => ({
    x: centro.x - larguraVista / 2,
    y: centro.y - larguraVista / aspecto / 2,
    w: larguraVista,
    h: larguraVista / aspecto,
  });

  /** Unidades de modelo por pixel de tela — tudo que precisa ter tamanho fixo usa isto. */
  const u = (): number => (larguraPx > 0 ? larguraVista / larguraPx : larguraVista / 720);

  function enquadrar(alvo: Rect): void {
    centro = { x: alvo.x + alvo.w / 2, y: alvo.y + alvo.h / 2 };
    larguraVista = Math.max(alvo.w, alvo.h * aspecto);
  }

  function reenquadrar(): void {
    const formas = [...formasParaLimites(avaliado.objects), ...(opts.backdrop ?? [])];
    if (formas.length === 0) enquadrar(caixaConteudo);
    else enquadrar(folga(boundsOf(formas, 0.08), 0.12));
    desenharTudo();
  }

  function medir(): void {
    const r = palco.getBoundingClientRect();
    if (r.width < 1 || r.height < 1) return;
    const novoAspecto = r.width / r.height;
    const primeira = larguraPx === 0;
    // Sem mudança real não se redesenha: o observador de tamanho vigia um elemento
    // cujo conteúdo nós mesmos trocamos, e um redesenho incondicional realimentaria.
    if (
      !primeira &&
      Math.abs(r.width - larguraPx) < 0.5 &&
      Math.abs(novoAspecto - aspecto) < 1e-3
    ) {
      return;
    }
    larguraPx = r.width;
    if (primeira || !enquadramentoTocado) {
      aspecto = novoAspecto;
      enquadrar(caixaConteudo);
    } else if (Math.abs(novoAspecto - aspecto) > 1e-3) {
      // Ao mudar a proporção do palco, o que se preserva é o CENTRO e a menor
      // dimensão visível: assim uma rotação de tablet não engole a figura.
      const alturaAntes = larguraVista / aspecto;
      aspecto = novoAspecto;
      larguraVista = Math.max(larguraVista, alturaAntes * aspecto);
    }
    desenharTudo();
  }

  // --- barra de ferramentas ---------------------------------------------------
  const botoes = new Map<ToolId, HTMLButtonElement>();
  montarBarra();

  function montarBarra(): void {
    const grupos: Array<readonly ToolId[]> = [
      opts.toolbelt.filter((id) => META[id].grupo === 'tracar'),
      opts.toolbelt.filter((id) => META[id].grupo === 'derivar'),
      opts.toolbelt.filter((id) => META[id].grupo === 'controle'),
    ];

    let primeiro = true;
    for (const g of grupos) {
      if (g.length === 0) continue;
      const caixa = document.createElement('div');
      caixa.className = 'pad-grupo';
      if (!primeiro) caixa.classList.add('pad-grupo-sep');
      // O grupo de controle vai para a outra ponta da barra: desfazer não é uma
      // ferramenta de criação e não deve disputar espaço com o compasso.
      if (META[g[0] as ToolId].grupo === 'controle') caixa.classList.add('pad-grupo-fim');
      primeiro = false;
      for (const id of g) caixa.appendChild(botaoDe(id));
      barra.appendChild(caixa);
    }
    barra.appendChild(contadorNaBarra());
  }

  function botaoDe(id: ToolId): HTMLButtonElement {
    const tool = TOOLS[id];
    const meta = META[id];
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'pad-ferramenta';
    b.dataset['tool'] = id;
    b.appendChild(icone(tool.icon));
    // Sem `title` nativo: o balão do navegador some ao clicar e não volta enquanto o
    // cursor não sair e reentrar. Quem acabou de escolher a ferramenta é justamente
    // quem mais precisa ler o que ela faz. Ver `explicacao`.
    b.setAttribute('aria-label', tool.label);
    b.setAttribute('aria-describedby', explicacao.id);
    b.setAttribute('aria-pressed', String(id === ferramenta));
    b.tabIndex = id === ferramenta ? 0 : -1;

    const texto = `${tool.label} — ${meta.gesto}`;
    b.addEventListener('pointerenter', () => mostrarExplicacao(b, texto));
    b.addEventListener('pointerleave', () => esconderExplicacao());
    b.addEventListener('focus', () => mostrarExplicacao(b, texto));
    b.addEventListener('blur', () => esconderExplicacao());

    b.addEventListener('click', () => {
      if (id === 'undo') {
        desfazer();
        return;
      }
      selecionar(id);
      // O balão CONTINUA visível depois do clique: o cursor ainda está sobre o botão.
      mostrarExplicacao(b, texto);
    });
    botoes.set(id, b);
    return b;
  }

  /**
   * Balão de explicação próprio, em vez do `title` do navegador.
   *
   * O nativo tem um comportamento que atrapalha aqui: ele some no clique e só
   * reaparece se o ponteiro sair e voltar. Como escolher a ferramenta É um clique, o
   * aluno perdia a explicação no exato momento em que ia usá-la.
   */
  function mostrarExplicacao(alvo: HTMLElement, texto: string): void {
    explicacao.textContent = texto;
    explicacao.hidden = false;
    const rb = alvo.getBoundingClientRect();
    const rr = root.getBoundingClientRect();
    explicacao.style.left = `${rb.left - rr.left + rb.width / 2}px`;
    explicacao.style.top = `${rb.bottom - rr.top + 6}px`;
  }

  function esconderExplicacao(): void {
    explicacao.hidden = true;
  }

  function contadorNaBarra(): HTMLElement {
    const caixa = document.createElement('div');
    caixa.className = 'pad-grupo pad-grupo-contador';
    caixa.appendChild(contador);
    return caixa;
  }

  function selecionar(id: ToolId): void {
    if (id === 'undo') return;
    ferramenta = id;
    escolhas = [];
    for (const [k, b] of botoes) {
      b.setAttribute('aria-pressed', String(k === id));
      b.tabIndex = k === id ? 0 : -1;
    }
    palco.dataset['modo'] = id === 'select' ? 'mover' : id === 'delete' ? 'apagar' : 'criar';
    opts.onToolSelected?.(id);
    atualizarDica();
    desenharSobre();
  }

  // --- navegação do quadro ----------------------------------------------------
  for (const [glifo, rotulo, acao] of [
    ['mais', 'Aproximar', () => aplicarZoom(1 / 1.3, null)],
    ['menos', 'Afastar', () => aplicarZoom(1.3, null)],
    ['enquadrar', 'Enquadrar tudo', () => reenquadrar()],
  ] as const) {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'pad-nav-botao';
    b.title = rotulo;
    b.setAttribute('aria-label', rotulo);
    b.appendChild(icone(glifo));
    b.addEventListener('click', acao);
    nav.appendChild(b);
  }

  /**
   * `fator` > 1 afasta. Com âncora, o ponto de modelo sob o ponteiro fica parado —
   * é o que faz o zoom parecer que puxa a figura em vez de embaralhá-la.
   */
  function aplicarZoom(fator: number, ancora: Vec2 | null): void {
    enquadramentoTocado = true;
    const base = Math.max(caixaConteudo.w, caixaConteudo.h * aspecto);
    const alvo = Math.min(Math.max(larguraVista * fator, base / ZOOM_MAX), base / ZOOM_MIN);
    const real = alvo / larguraVista;
    if (ancora) {
      centro = {
        x: ancora.x + (centro.x - ancora.x) * real,
        y: ancora.y + (centro.y - ancora.y) * real,
      };
    }
    larguraVista = alvo;
    desenharTudo();
  }

  // --- coordenadas ------------------------------------------------------------
  function paraModelo(cx: number, cy: number): { p: Vec2; escala: number } | null {
    const r = palco.getBoundingClientRect();
    // Antes da primeira medida do navegador a caixa vem com zero, e aí a escala
    // seria infinita: o gesto é descartado em vez de virar um ponto no infinito.
    if (r.width < 1 || r.height < 1) return null;
    // A vista muda a cada zoom, e por isso é lida agora, não guardada.
    return pontoNoModelo(r, vista(), cx, cy);
  }

  const imanar = (p: Vec2, escala: number): SnapResult =>
    snap(p, avaliado.objects.values(), opts.snapping, escala);

  // --- gesto ------------------------------------------------------------------
  interface Pressao {
    readonly pick: PickRef;
    readonly cliente: { readonly x: number; readonly y: number };
    readonly id: number;
  }

  const ponteiros = new Map<number, { x: number; y: number }>();
  let pressao: Pressao | null = null;
  let arrastou = false;
  let navegando:
    | { modo: 'quadro'; ultimo: { x: number; y: number } }
    | { modo: 'pinca'; d0: number; w0: number }
    | null = null;

  function aoPressionar(ev: PointerEvent): void {
    palco.focus({ preventScroll: true });
    ponteiros.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });

    // Dois dedos: sempre navegação, nunca construção. Cancela o gesto em curso.
    if (ponteiros.size === 2) {
      pressao = null;
      arrastou = false;
      const [a, b] = [...ponteiros.values()] as [
        { x: number; y: number },
        { x: number; y: number },
      ];
      navegando = { modo: 'pinca', d0: Math.hypot(a.x - b.x, a.y - b.y) || 1, w0: larguraVista };
      desenharSobre();
      return;
    }
    if (ponteiros.size > 2) return;

    palco.setPointerCapture(ev.pointerId);

    // Botão do meio, botão direito ou a ferramenta "Selecionar" movem o quadro — e,
    // com a resposta enviada, qualquer gesto só move o quadro.
    if (bloqueado || ev.button === 1 || ev.button === 2 || ferramenta === 'select') {
      navegando = { modo: 'quadro', ultimo: { x: ev.clientX, y: ev.clientY } };
      palco.classList.add('is-arrastando');
      return;
    }

    const m = paraModelo(ev.clientX, ev.clientY);
    if (!m) return;
    const s = imanar(m.p, m.escala);
    cursor = m.p;
    definirSnap(s);
    pressao = {
      pick: { id: objetoEm(s), at: s.at, snap: s },
      cliente: { x: ev.clientX, y: ev.clientY },
      id: ev.pointerId,
    };
    arrastou = false;
    posicionarEtiqueta(ev.clientX, ev.clientY);
    desenharSobre();
  }

  function aoMover(ev: PointerEvent): void {
    if (ponteiros.has(ev.pointerId)) ponteiros.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });

    if (navegando?.modo === 'pinca') {
      if (ponteiros.size < 2) return;
      const [a, b] = [...ponteiros.values()] as [
        { x: number; y: number },
        { x: number; y: number },
      ];
      const d = Math.hypot(a.x - b.x, a.y - b.y) || 1;
      const alvo = navegando.w0 * (navegando.d0 / d);
      aplicarZoom(alvo / larguraVista, null);
      return;
    }

    if (navegando?.modo === 'quadro') {
      enquadramentoTocado = true;
      const k = u();
      centro = {
        x: centro.x - (ev.clientX - navegando.ultimo.x) * k,
        // y do modelo cresce para cima; o da tela, para baixo.
        y: centro.y + (ev.clientY - navegando.ultimo.y) * k,
      };
      navegando = { modo: 'quadro', ultimo: { x: ev.clientX, y: ev.clientY } };
      desenharTudo();
      return;
    }

    const m = paraModelo(ev.clientX, ev.clientY);
    if (!m) return;
    cursor = m.p;
    definirSnap(imanar(m.p, m.escala));
    posicionarEtiqueta(ev.clientX, ev.clientY);

    if (pressao && !arrastou) {
      const d = Math.hypot(ev.clientX - pressao.cliente.x, ev.clientY - pressao.cliente.y);
      if (d > LIMIAR_ARRASTO) arrastou = true;
    }
    desenharSobre();
  }

  function aoSoltar(ev: PointerEvent): void {
    ponteiros.delete(ev.pointerId);
    if (palco.hasPointerCapture(ev.pointerId)) palco.releasePointerCapture(ev.pointerId);

    if (navegando) {
      if (ponteiros.size === 0) {
        navegando = null;
        palco.classList.remove('is-arrastando');
      }
      return;
    }

    const p = pressao;
    pressao = null;
    if (!p) return;

    const m = paraModelo(ev.clientX, ev.clientY);
    const s = m ? imanar(m.p, m.escala) : p.pick.snap;
    concluir(p.pick, { id: objetoEm(s), at: s.at, snap: s }, arrastou);
    arrastou = false;
  }

  function aoCancelar(ev: PointerEvent): void {
    ponteiros.delete(ev.pointerId);
    pressao = null;
    arrastou = false;
    if (ponteiros.size === 0) {
      navegando = null;
      palco.classList.remove('is-arrastando');
    }
    desenharSobre();
  }

  /**
   * A regra única do gesto:
   *
   *   faltam 2 ou mais escolhas  ->  o arrasto vale por DUAS (onde apertou, onde soltou);
   *   falta exatamente 1         ->  o arrasto é MIRA: vale o ponto onde soltou.
   *
   * Daí saem, sem caso particular, o segmento num movimento, a circunferência do
   * centro para fora, o compasso (toque no centro, arrasto da abertura) e o ponto que
   * se ajusta antes de largar.
   */
  function concluir(press: PickRef, solta: PickRef, houveArrasto: boolean): void {
    if (bloqueado) return;
    if (ferramenta === 'delete') {
      const alvo = solta.id;
      if (alvo && !opts.givenIds.has(alvo as string)) {
        atual = removeObject(atual, alvo);
        avaliado = evaluate(atual);
        registrar({ seq: acoes.length, tMs: agora(), tool: 'delete', target: alvo });
        historico.push(atual);
        opts.onChange?.(atual);
        atualizarContador();
      }
      desenharTudo();
      return;
    }

    if (ferramenta === 'select' || ferramenta === 'undo') return;

    const tool: Tool = TOOLS[ferramenta];
    const restantes = tool.arity - escolhas.length;
    if (houveArrasto && restantes >= 2) escolhas.push(press, solta);
    else escolhas.push(solta);

    if (escolhas.length >= tool.arity) comitar(tool);
    else {
      atualizarDica();
      desenharSobre();
    }
  }

  function comitar(tool: Tool): void {
    const picks = escolhas;
    escolhas = [];

    const r = tool.apply(atual, picks);
    if (r.created.length === 0) {
      atualizarDica();
      desenharSobre();
      return;
    }

    // Nomear é da INTERFACE, não do kernel: `names` não entra em nenhum predicado nem
    // na reexecução da trajetória. Sem nome, o aluno perde a conversa — não dá para
    // dizer "a interseção dos arcos" quando nada na tela tem nome. O item pode
    // dispensar as letras quando elas só poluiriam (marcar muitos pontos numa malha).
    atual = opts.rotularPontos === false ? r.snapshot : nomear(r.snapshot, r.created);
    historico.push(atual);
    avaliado = evaluate(atual);
    registrar({
      seq: acoes.length,
      tMs: agora(),
      tool: ferramenta as CreatingToolId,
      picks,
      created: r.created,
    });
    opts.onChange?.(atual);

    atualizarContador();
    atualizarDica();
    desenharBase();
    iniciarNascimento(r.created);
  }

  function objetoEm(s: SnapResult): GeoId | null {
    if (s.to === 'point') return s.id;
    if (s.to === 'object') return s.host;
    // Ferramentas que pedem um objeto (perpendicular, interseção) aceitam o objeto
    // sob o cursor mesmo quando o ímã preferiu uma interseção.
    if (s.to === 'intersection') return s.a;
    return null;
  }

  function desfazer(): void {
    if (bloqueado) return;
    if (escolhas.length > 0) {
      escolhas = [];
      atualizarDica();
      desenharSobre();
      return;
    }
    if (historico.length <= 1) return;
    historico.pop();
    atual = historico[historico.length - 1] as ConstructionSnapshot;
    avaliado = evaluate(atual);
    registrar({ seq: acoes.length, tMs: agora(), tool: 'undo', toSeq: historico.length - 1 });
    opts.onChange?.(atual);
    atualizarContador();
    atualizarDica();
    desenharTudo();
  }

  function registrar(a: ToolAction): void {
    acoes.push(a);
    opts.onAction?.(a);
  }

  const agora = (): number => Math.round(performance.now() - t0);

  // --- ímã: identidade e crescimento -----------------------------------------
  function definirSnap(s: SnapResult): void {
    const chave = chaveDeSnap(s);
    if (chave !== snapChave) {
      snapChave = chave;
      snapEm = performance.now();
    }
    snapAtual = s;
    atualizarEtiqueta(s);
  }

  function atualizarEtiqueta(s: SnapResult): void {
    if (ferramenta === 'select' || cursor === null) {
      etiqueta.hidden = true;
      return;
    }
    etiqueta.hidden = false;
    etiqueta.textContent = textoDoSnap(s);
    etiqueta.dataset['tipo'] = s.to;
  }

  function textoDoSnap(s: SnapResult): string {
    if (s.to === 'point') {
      const o = avaliado.objects.get(s.id);
      return o && o.name ? `ponto ${o.name}` : 'ponto';
    }
    if (s.to === 'object') {
      const o = avaliado.objects.get(s.host);
      return o && o.name ? `sobre ${o.name}` : 'sobre o objeto';
    }
    if (s.to === 'none') return ferramenta === 'delete' ? 'nada aqui' : 'livre';
    return snapLabel(s);
  }

  function posicionarEtiqueta(cx: number, cy: number): void {
    const r = palco.getBoundingClientRect();
    etiqueta.style.transform = `translate(${Math.round(cx - r.left + 16)}px, ${Math.round(cy - r.top + 14)}px)`;
  }

  // --- nascimento -------------------------------------------------------------
  /**
   * O objeto não aparece: ele é DESENHADO. O arco corre do primeiro ponto de apoio, o
   * segmento cresce da origem para o destino. 210 ms, e nunca no caminho — o próximo
   * gesto pode começar antes de a animação acabar.
   */
  function iniciarNascimento(ids: readonly GeoId[]): void {
    if (reduzido) return;
    const visiveis = ids.filter((id) => avaliado.objects.has(id));
    if (visiveis.length === 0) return;
    nascendo = { ids: visiveis, t0: performance.now() };
    ocultarRecemNascidos(true);
    animar();
  }

  function ocultarRecemNascidos(oculto: boolean): void {
    if (!nascendo) return;
    for (const id of nascendo.ids) {
      const el = capaBase.querySelector<SVGElement>(`[data-key="${id as string}"]`);
      if (!el) continue;
      if (oculto) el.style.opacity = '0';
      else {
        el.style.removeProperty('opacity');
        el.classList.add('pad-surgiu');
      }
    }
  }

  let quadro = 0;
  function animar(): void {
    if (quadro !== 0) return;
    const passo = (): void => {
      quadro = 0;
      const vivo = nascendo !== null || crescendoIma();
      desenharSobre();
      if (nascendo && performance.now() - nascendo.t0 >= DUR_NASCIMENTO) {
        ocultarRecemNascidos(false);
        nascendo = null;
        desenharSobre();
      }
      if (vivo) quadro = requestAnimationFrame(passo);
    };
    quadro = requestAnimationFrame(passo);
  }

  const crescendoIma = (): boolean => snapAtual !== null && performance.now() - snapEm < DUR_IMA;

  // --- desenho ----------------------------------------------------------------
  function desenharTudo(): void {
    desenharBase();
    desenharSobre();
  }

  function desenharBase(): void {
    const v = vista();
    const cena = constructionToScene(avaliado, {
      title: 'Prancheta de construção',
      desc:
        opts.describe ??
        'Área de trabalho onde a construção é feita com as ferramentas disponíveis.',
      givenIds: opts.givenIds,
      snapshot: atual,
      viewBox: v,
      keyed: true,
      // A PRANCHETA NÃO DESENHA CENÁRIO NENHUM, nem mesmo a malha do snap.
      //
      // Ela chegou a desenhar a malha automaticamente sempre que o snap fosse de
      // malha, com o argumento de que marcar pontos sem ver a malha vira teste de
      // pontaria. Só que num item cujo objetivo é DETERMINAR coordenadas, mostrar a
      // malha entrega a resposta — e a prancheta não tem como saber qual dos dois
      // casos é o seu. Quem sabe é o item: o que deve aparecer vem em `backdrop`.
    });

    // Cenário embaixo, construção por cima.
    const svg = renderScene({
      ...cena,
      shapes: [...(opts.backdrop ?? []), ...formasDoPoligono(), ...cena.shapes],
    });
    svg.style.setProperty('--scene-px', String(u()));
    capaBase.replaceChildren(svg);
    ocultarRecemNascidos(true);
  }

  /**
   * O polígono dos pontos do aluno, quando ele marcou exatamente `ligarPontos` deles:
   * ligados na ordem em que foram marcados, por baixo dos pontos. Com um a mais ou a
   * menos, nada — um polígono de lados errados sugeriria uma figura que não existe.
   */
  function formasDoPoligono(): Shape[] {
    const n = opts.ligarPontos;
    if (!n) return [];
    const dados = new Set(opts.givens.order.map((id) => id as string));
    const pts: Vec2[] = [];
    for (const id of atual.order) {
      if (dados.has(id as string)) continue;
      const o = avaliado.objects.get(id);
      if (o?.kind === 'point') pts.push(o.pos);
    }
    return pts.length === n
      ? [{ t: 'poly', pts, closed: true, role: 'result', fill: 'result-fill' }]
      : [];
  }

  function desenharSobre(): void {
    // Travada, a prancheta não mostra ímã nem prévia: sugerir onde um ponto nasceria
    // seria convidar a uma ação que não vai acontecer.
    if (bloqueado) {
      capaSobre.replaceChildren();
      etiqueta.hidden = true;
      return;
    }
    const v = vista();
    const formas: Shape[] = [
      ...formasDeDestaque(),
      ...formasDePrevia(),
      ...formasDeEscolhas(),
      ...formasDoIma(),
      ...formasDeNascimento(),
    ];
    const cena = scene(formas, {
      a11y: { title: 'Camada de apoio', desc: 'Prévia do que a ferramenta vai criar.' },
      viewBox: v,
    });
    const svg = renderScene(cena);
    svg.setAttribute('aria-hidden', 'true');
    svg.style.setProperty('--scene-px', String(u()));
    capaSobre.replaceChildren(svg);
    if ((nascendo || crescendoIma()) && !reduzido) animar();
  }

  /** O objeto sob o cursor se acende: o aluno sabe o que vai pegar ANTES de clicar. */
  function formasDeDestaque(): Shape[] {
    const s = snapAtual;
    if (!s || ferramenta === 'select') return [];
    const ids: GeoId[] =
      s.to === 'object'
        ? [s.host]
        : s.to === 'intersection'
          ? [s.a, s.b]
          : s.to === 'point'
            ? []
            : [];
    const filhos: Shape[] = [];
    for (const id of ids) {
      const o = avaliado.objects.get(id);
      if (o) filhos.push(...formaDeObjeto(o, 'highlight'));
    }
    return filhos.length > 0 ? [{ t: 'group', children: filhos, opacity: 0.55 }] : [];
  }

  function formasDeEscolhas(): Shape[] {
    return escolhas.map((e) => ({ t: 'point', p: e.at, role: 'result' }) as Shape);
  }

  /**
   * A prévia usa as MESMAS formas do resultado. Ver o círculo inteiro enquanto se
   * arrasta, e não um raio abstrato, é o que faz o gesto ser lido como desenho.
   */
  function formasDePrevia(): Shape[] {
    if (!cursor || ferramenta === 'select' || ferramenta === 'delete') return [];
    const tool = TOOLS[ferramenta];
    const restantes = tool.arity - escolhas.length;
    const pontos = escolhas.map((e) => e.at);
    if (pressao && arrastou && restantes >= 2) pontos.push(pressao.pick.at);
    const n = pontos.length;
    if (n === 0) return [];

    const alvo = snapAtual ? snapAtual.at : cursor;
    const a = pontos[0] as Vec2;
    const ultimo = pontos[n - 1] as Vec2;

    switch (ferramenta) {
      case 'segment':
        return [{ t: 'seg', a: ultimo, b: alvo, role: 'muted' }];
      case 'ray':
        return dist(ultimo, alvo) < 1e-9
          ? []
          : [{ t: 'ray', from: ultimo, through: alvo, role: 'muted' }];
      case 'line':
        return dist(ultimo, alvo) < 1e-9
          ? []
          : [{ t: 'line', p: ultimo, dir: normalize(sub(alvo, ultimo)), role: 'muted' }];
      case 'circle-center-through': {
        const r = dist(a, alvo);
        return r < 1e-9
          ? []
          : [
              { t: 'circle', c: a, r, role: 'muted' },
              { t: 'seg', a, b: alvo, role: 'construction' },
            ];
      }
      case 'compass': {
        if (n < 2) return [];
        const r = dist(pontos[1] as Vec2, alvo);
        return r < 1e-9
          ? []
          : [
              { t: 'seg', a: pontos[1] as Vec2, b: alvo, role: 'construction' },
              { t: 'circle', c: a, r, role: 'muted' },
            ];
      }
      case 'midpoint':
        return [
          { t: 'seg', a: ultimo, b: alvo, role: 'construction' },
          { t: 'point', p: midpoint(ultimo, alvo), role: 'ghost' },
        ];
      case 'perp-bisector': {
        if (dist(ultimo, alvo) < 1e-9) return [];
        const m = midpoint(ultimo, alvo);
        return [
          { t: 'seg', a: ultimo, b: alvo, role: 'construction' },
          { t: 'line', p: m, dir: perp(normalize(sub(alvo, ultimo))), role: 'muted' },
        ];
      }
      case 'perpendicular':
      case 'parallel': {
        const alvoObj = objetoRetaSob();
        if (!alvoObj) return [{ t: 'seg', a: ultimo, b: alvo, role: 'construction' }];
        const dir = ferramenta === 'parallel' ? alvoObj.dir : perp(alvoObj.dir);
        return [{ t: 'line', p: a, dir, role: 'muted' }];
      }
      case 'reflect': {
        const eixo = objetoRetaSob();
        if (!eixo) return [{ t: 'seg', a: ultimo, b: alvo, role: 'construction' }];
        const d = sub(a, eixo.p);
        const t = d.x * eixo.dir.x + d.y * eixo.dir.y;
        const pe = add(eixo.p, scale(eixo.dir, t));
        return [
          { t: 'seg', a, b: add(pe, sub(pe, a)), role: 'construction' },
          { t: 'point', p: add(pe, sub(pe, a)), role: 'ghost' },
        ];
      }
      case 'angle-bisector': {
        const saida: Shape[] = [{ t: 'seg', a: ultimo, b: alvo, role: 'construction' }];
        if (n >= 2)
          saida.push({
            t: 'seg',
            a: pontos[1] as Vec2,
            b: pontos[0] as Vec2,
            role: 'construction',
          });
        return saida;
      }
      case 'intersect': {
        const primeiro = escolhas[0]?.id ?? (pressao && arrastou ? pressao.pick.id : null);
        const segundo = snapAtual ? objetoEm(snapAtual) : null;
        if (!primeiro || !segundo || primeiro === segundo) return [];
        const oa = avaliado.objects.get(primeiro);
        const ob = avaliado.objects.get(segundo);
        if (!oa || !ob) return [];
        return intersectionPoints(oa, ob).map((p) => ({ t: 'point', p, role: 'ghost' }) as Shape);
      }
      default:
        return [];
    }
  }

  /** A reta/semirreta/segmento sob o cursor, se houver — para as prévias que pedem eixo. */
  function objetoRetaSob(): { p: Vec2; dir: Vec2 } | null {
    const s = snapAtual;
    if (!s) return null;
    const id = objetoEm(s);
    if (!id) return null;
    const o = avaliado.objects.get(id);
    return o && o.kind === 'line' ? { p: o.p, dir: o.dir } : null;
  }

  /**
   * O MARCADOR DIZ QUE TIPO DE PONTO VAI NASCER.
   *
   * Cruz dentro do anel = interseção (ponto derivado, sobrevive à perturbação).
   * Losango = sobre o objeto. Quadrado = nó da malha. Anel cheio = ponto que já
   * existe. Anel PONTILHADO = vazio: nasce um ponto solto, que a checagem de robustez
   * vai deixar para trás. Quem entende o marcador entende a correção.
   */
  function formasDoIma(): Shape[] {
    const s = snapAtual;
    if (!s || !cursor || ferramenta === 'select') return [];
    const k = u();
    // O marcador nasce maior e assenta: o crescimento é o que dá vontade de acertar.
    const t = Math.min(1, (performance.now() - snapEm) / DUR_IMA);
    const g = 1 + 0.5 * (1 - t) * (1 - t);
    const R = 9 * k * g;

    if (s.to === 'none') {
      return [
        { t: 'circle', c: s.at, r: R * 0.85, role: 'construction' },
        { t: 'point', p: s.at, role: 'ghost' },
      ];
    }
    if (s.to === 'point') {
      return [{ t: 'circle', c: s.at, r: R, role: 'highlight' }];
    }
    if (s.to === 'intersection') {
      const c = 5 * k * g;
      return [
        { t: 'circle', c: s.at, r: R, role: 'highlight' },
        {
          t: 'seg',
          a: { x: s.at.x - c, y: s.at.y - c },
          b: { x: s.at.x + c, y: s.at.y + c },
          role: 'highlight',
        },
        {
          t: 'seg',
          a: { x: s.at.x - c, y: s.at.y + c },
          b: { x: s.at.x + c, y: s.at.y - c },
          role: 'highlight',
        },
      ];
    }
    if (s.to === 'lattice') {
      const c = 6 * k * g;
      return [
        {
          t: 'poly',
          pts: [
            { x: s.at.x - c, y: s.at.y - c },
            { x: s.at.x + c, y: s.at.y - c },
            { x: s.at.x + c, y: s.at.y + c },
            { x: s.at.x - c, y: s.at.y + c },
          ],
          closed: true,
          role: 'highlight',
        },
      ];
    }
    const c = 7.5 * k * g;
    return [
      {
        t: 'poly',
        pts: [
          { x: s.at.x, y: s.at.y - c },
          { x: s.at.x + c, y: s.at.y },
          { x: s.at.x, y: s.at.y + c },
          { x: s.at.x - c, y: s.at.y },
        ],
        closed: true,
        role: 'highlight',
      },
    ];
  }

  function formasDeNascimento(): Shape[] {
    if (!nascendo) return [];
    const bruto = Math.min(1, (performance.now() - nascendo.t0) / DUR_NASCIMENTO);
    const t = 1 - (1 - bruto) * (1 - bruto) * (1 - bruto);
    const saida: Shape[] = [];
    for (const id of nascendo.ids) {
      const o = avaliado.objects.get(id);
      if (o) saida.push(...crescendo(o, t));
    }
    return saida;
  }

  function crescendo(o: GeoObject, t: number): Shape[] {
    if (o.kind === 'point') {
      const R = 14 * u() * (1 - t);
      return [
        { t: 'point', p: o.pos, role: 'result' },
        ...(R > 0.01 ? [{ t: 'circle', c: o.pos, r: R, role: 'highlight' } as Shape] : []),
      ];
    }

    if (o.kind === 'circle') {
      // O arco corre a partir do ponto de apoio do compasso: é o gesto do compasso.
      const apoio = avaliado.objects.get(
        o.def.via === 'center-through' ? o.def.through : o.def.radiusB,
      );
      const a0 = apoio && apoio.kind === 'point' ? angleOf(sub(apoio.pos, o.c)) : 0;
      const total = 2 * Math.PI * t;
      // `arc` normaliza o ângulo para (-pi, pi]: uma volta inteira sai em pedaços.
      const partes: Shape[] = [];
      let feito = 0;
      while (feito < total - 1e-9) {
        const passo = Math.min(Math.PI * 0.9, total - feito);
        partes.push({
          t: 'arc',
          c: o.c,
          r: o.r,
          a0: a0 + feito,
          a1: a0 + feito + passo,
          role: 'highlight',
        });
        feito += passo;
      }
      return partes;
    }

    const diag = Math.hypot(vista().w, vista().h) * 0.75;
    if (o.span === 'segment')
      return [{ t: 'seg', a: o.p, b: add(o.p, scale(sub(o.q, o.p), t)), role: 'highlight' }];
    if (o.span === 'ray')
      return [{ t: 'seg', a: o.p, b: add(o.p, scale(o.dir, diag * t)), role: 'highlight' }];
    return [
      {
        t: 'seg',
        a: add(o.p, scale(o.dir, -diag * t)),
        b: add(o.p, scale(o.dir, diag * t)),
        role: 'highlight',
      },
    ];
  }

  function formaDeObjeto(o: GeoObject, role: 'highlight'): Shape[] {
    if (o.kind === 'point') return [{ t: 'circle', c: o.pos, r: 8 * u(), role }];
    if (o.kind === 'circle') return [{ t: 'circle', c: o.c, r: o.r, role }];
    if (o.span === 'segment') return [{ t: 'seg', a: o.p, b: o.q, role }];
    if (o.span === 'ray') return [{ t: 'ray', from: o.p, through: o.q, role }];
    return [{ t: 'line', p: o.p, dir: o.dir, role }];
  }

  // --- rodapé -----------------------------------------------------------------
  function atualizarDica(): void {
    const tool = TOOLS[ferramenta];
    const meta = META[ferramenta];
    const passo = tool.prompt[escolhas.length];
    dicaTexto.textContent = passo
      ? escolhas.length === 0
        ? `${tool.label}: ${passo} — ${meta.gesto}.`
        : `${tool.label}: ${passo}.`
      : `${tool.label}: ${meta.gesto}.`;

    passos.replaceChildren();
    if (tool.arity > 1) {
      for (let i = 0; i < tool.arity; i++) {
        const p = document.createElement('i');
        p.className = i < escolhas.length ? 'pad-passo is-feito' : 'pad-passo';
        passos.appendChild(p);
      }
    }
  }

  function atualizarContador(): void {
    // O CONTADOR MOSTRA O ESTADO, NÃO O HISTÓRICO — e é a MESMA conta da correção.
    //
    // Contar os estados da pilha fazia a lixeira SOMAR um movimento (apagar também
    // empilha um estado). `movimentosEfetivos` conta as criações das quais algum objeto
    // continua na figura: desfazer e apagar um objeto que sobrou descontam; apagar um
    // objeto de que a figura depende apaga a figura junto (a exclusão é em cascata), e
    // por isso não serve de atalho.
    const n = movimentosEfetivos(opts.givens, acoes);
    contador.replaceChildren(
      Object.assign(document.createElement('b'), { textContent: String(n) }),
      document.createTextNode(` ${nome('movimento', numeroDe(n))}`),
    );
  }

  // --- teclado ----------------------------------------------------------------
  function aoTeclar(ev: KeyboardEvent): void {
    if ((ev.ctrlKey || ev.metaKey) && ev.key.toLowerCase() === 'z') {
      ev.preventDefault();
      desfazer();
      return;
    }
    if (ev.ctrlKey || ev.metaKey || ev.altKey) return;

    if (ev.key === 'Escape') {
      if (escolhas.length > 0) {
        escolhas = [];
        atualizarDica();
        desenharSobre();
      }
      return;
    }
    if (ev.key === 'Delete' || ev.key === 'Backspace') {
      if (opts.toolbelt.includes('delete')) {
        ev.preventDefault();
        selecionar('delete');
      }
      return;
    }
    if (ev.key === '+' || ev.key === '=') {
      aplicarZoom(1 / 1.3, null);
      return;
    }
    if (ev.key === '-') {
      aplicarZoom(1.3, null);
      return;
    }
    if (ev.key === '0') {
      reenquadrar();
      return;
    }

    // SEM ATALHO DE FERRAMENTA, por dígito ou por letra.
    //
    // Num item de avaliação o cinto muda de item para item: a mesma tecla escolhia
    // uma ferramenta aqui e outra na questão seguinte. Pior, um aluno que digita no
    // teclado por engano trocava de instrumento no meio de uma construção e só
    // percebia depois. O que sobra é o que não é ambíguo: Esc cancela o passo,
    // Ctrl+Z desfaz, Delete apaga, +/−/0 governam a vista.
  }

  /** Setas percorrem a barra; é o que um `role="toolbar"` promete ao leitor de tela. */
  function aoTeclarNaBarra(ev: KeyboardEvent): void {
    if (ev.key !== 'ArrowRight' && ev.key !== 'ArrowLeft') return;
    const lista = [...botoes.values()];
    const i = lista.indexOf(document.activeElement as HTMLButtonElement);
    if (i < 0) return;
    ev.preventDefault();
    const j = (i + (ev.key === 'ArrowRight' ? 1 : lista.length - 1)) % lista.length;
    lista[j]?.focus();
  }

  /**
   * A roda só aproxima com Ctrl (ou pinça de trackpad, que o navegador entrega como
   * Ctrl). Sequestrar a roda nua prenderia a rolagem da página numa prancheta que
   * ocupa meia tela — e ninguém consegue chegar ao botão "Responder".
   */
  function aoRodar(ev: WheelEvent): void {
    if (!ev.ctrlKey && !ev.metaKey) return;
    ev.preventDefault();
    const m = paraModelo(ev.clientX, ev.clientY);
    aplicarZoom(ev.deltaY > 0 ? 1.12 : 1 / 1.12, m ? m.p : null);
  }

  // --- ligação -----------------------------------------------------------------
  palco.addEventListener('pointerdown', aoPressionar);
  palco.addEventListener('pointermove', aoMover);
  palco.addEventListener('pointerup', aoSoltar);
  palco.addEventListener('pointercancel', aoCancelar);
  palco.addEventListener('wheel', aoRodar, { passive: false });
  palco.addEventListener('contextmenu', prevenir);
  palco.addEventListener('pointerleave', () => {
    if (pressao) return;
    cursor = null;
    snapAtual = null;
    snapChave = '';
    etiqueta.hidden = true;
    desenharSobre();
  });
  root.addEventListener('keydown', aoTeclar);
  barra.addEventListener('keydown', aoTeclarNaBarra);

  const observador =
    typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => medir()) : null;
  observador?.observe(palco);
  if (!observador && typeof requestAnimationFrame !== 'undefined')
    requestAnimationFrame(() => medir());

  selecionar(ferramenta);
  atualizarContador();
  atualizarDica();
  desenharTudo();

  return {
    element: root,
    snapshot: () => atual,
    // CÓPIA, não o próprio array. Entregar `acoes` fazia a resposta guardada crescer
    // junto com qualquer ponto marcado depois do envio — e a revisão mostrava uma
    // resposta que não foi a corrigida.
    trajectory: () => [...acoes],
    bloquear() {
      bloqueado = true;
      escolhas = [];
      pressao = null;
      root.classList.add('is-bloqueado');
      for (const b of botoes.values()) b.disabled = true;
      palco.dataset['modo'] = 'mover';
      passos.replaceChildren();
      dicaTexto.textContent =
        'Resposta enviada: a construção não pode mais ser alterada. Ainda dá para aproximar e mover o quadro.';
      desenharSobre();
    },
    dispose() {
      palco.removeEventListener('pointerdown', aoPressionar);
      palco.removeEventListener('pointermove', aoMover);
      palco.removeEventListener('pointerup', aoSoltar);
      palco.removeEventListener('pointercancel', aoCancelar);
      palco.removeEventListener('wheel', aoRodar);
      palco.removeEventListener('contextmenu', prevenir);
      root.removeEventListener('keydown', aoTeclar);
      barra.removeEventListener('keydown', aoTeclarNaBarra);
      observador?.disconnect();
      if (quadro !== 0) cancelAnimationFrame(quadro);
    },
  };
}

// ---------------------------------------------------------------------------
// Auxiliares
// ---------------------------------------------------------------------------

const prevenir = (ev: Event): void => ev.preventDefault();

function ferramentaInicial(cinto: readonly ToolId[]): ToolId {
  for (const preferida of ['point', 'segment', 'select'] as const) {
    if (cinto.includes(preferida)) return preferida;
  }
  return (cinto[0] ?? 'select') as ToolId;
}

/**
 * Folga do enquadramento inicial. Um cinto com compasso precisa de espaço em volta:
 * um arco de raio OA centrado em O sai da caixa do triângulo. Sem compasso, a folga
 * generosa só produz palco vazio.
 */
function folgaDoCinto(cinto: readonly ToolId[]): number {
  const temArco = cinto.some((id) => id === 'compass' || id === 'circle-center-through');
  return temArco ? 0.34 : 0.14;
}

const ALFABETO = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const SUBSCRITO = '₀₁₂₃₄₅₆₇₈₉';

/** Batiza os pontos recém-criados que ainda não têm nome. */
function nomear(s: ConstructionSnapshot, criados: readonly GeoId[]): ConstructionSnapshot {
  const usados = new Set(Object.values(s.names).filter((n) => n.length > 0));
  const names: Record<string, string> = { ...s.names };
  let mudou = false;

  for (const id of criados) {
    const k = id as string;
    if (s.kinds[k] !== 'point') continue;
    if ((names[k] ?? '') !== '') continue;
    const nome = proximoNome(usados);
    names[k] = nome;
    usados.add(nome);
    mudou = true;
  }

  return mudou ? { ...s, names } : s;
}

function proximoNome(usados: ReadonlySet<string>): string {
  for (let i = 0; i < 26 * 10; i++) {
    const letra = ALFABETO[i % 26] as string;
    const volta = Math.floor(i / 26);
    const nome = volta === 0 ? letra : letra + subscrito(volta);
    if (!usados.has(nome)) return nome;
  }
  return '·';
}

const subscrito = (n: number): string =>
  String(n)
    .split('')
    .map((d) => SUBSCRITO[Number(d)] ?? d)
    .join('');

function chaveDeSnap(s: SnapResult): string {
  switch (s.to) {
    case 'point':
      return `p:${s.id as string}`;
    case 'intersection':
      return `x:${s.a as string}:${s.b as string}:${s.branch}`;
    case 'object':
      return `o:${s.host as string}`;
    case 'lattice':
      return `m:${s.i}:${s.j}`;
    case 'none':
      return 'n';
  }
}

function formasDoSnapshot(snapshot: ConstructionSnapshot): Shape[] {
  return formasParaLimites(evaluate(snapshot).objects);
}

/** Formas simplificadas só para calcular limites (retas infinitas não contam). */
function formasParaLimites(objs: ReadonlyMap<GeoId, GeoObject>): Shape[] {
  const out: Shape[] = [];
  for (const o of objs.values()) {
    if (o.kind === 'point') out.push({ t: 'point', p: o.pos, role: 'given' });
    else if (o.kind === 'circle') out.push({ t: 'circle', c: o.c, r: o.r, role: 'given' });
    else if (o.span === 'segment') out.push({ t: 'seg', a: o.p, b: o.q, role: 'given' });
    else out.push({ t: 'point', p: o.p, role: 'given' });
  }
  return out;
}

/** Amplia a caixa proporcionalmente, mantendo o centro. */
function folga(r: Rect, fator: number): Rect {
  const dx = r.w * fator;
  const dy = r.h * fator;
  return { x: r.x - dx / 2, y: r.y - dy / 2, w: r.w + dx, h: r.h + dy };
}
